import {useSyncExternalStore} from 'react'
import {delayExpiresAtMs} from '@/features/trip-delay/mapTripDelay'

/**
 * HOW LONG IS LEFT ON A DELAY — a once-a-second tick against a fixed deadline.
 *
 * ── ⚠️ IT RECOMPUTES FROM `Date.now()`, IT DOES NOT DECREMENT ─────────────────────
 * A counter that subtracts one each tick drifts: the interval is throttled to once a
 * minute in a background tab, pauses outright on a sleeping laptop, and never catches up.
 * A passenger who locked their phone for ten minutes would come back to a banner claiming
 * nine minutes left on a delay that ended. Reading the clock every tick makes the interval
 * a repaint trigger rather than the source of the number, so a missed tick costs nothing.
 *
 * ── ⚠️ AND IT IS ALSO WHAT ENDS THE DELAY ON SCREEN ───────────────────────────────
 * Nothing broadcasts an expiry — there is no event, and the trip payload's `is_active` is
 * whatever was true when it was built. This tick is the only thing that re-renders the
 * screen at the moment a window closes, which is what makes the banner disappear and the
 * "Experiencing a delay?" card come back.
 *
 * @param {object|null} delay a `requested_delay` record
 * @returns {{remainingSeconds: number, remainingMinutes: number, expired: boolean, label: string}}
 */

/**
 * The clock, as an external store.
 *
 * ⚠️ NOT `useState` + `useEffect`, and the difference is not style. A state variable
 * seeded at mount is stale from the moment a delay STARTS — the hook has been sitting there
 * with no interval running while `delay` was null — so the first paint of a new banner
 * would show a number computed against whenever the sheet was opened. Read as a store, the
 * value is whatever the clock says at render, always.
 *
 * ⚠️ THE SNAPSHOT IS FLOORED TO WHOLE SECONDS. `getSnapshot` must return the same value
 * until something actually changes; a raw `Date.now()` differs on every call and React
 * re-renders forever chasing it.
 */
const subscribeToSeconds = (onTick) => {
    const timer = setInterval(onTick, 1000)
    return () => clearInterval(timer)
}
/** No delay, no ticking — a screen with nothing counting down should not hold a timer. */
const subscribeToNothing = () => () => {}
const secondsNow = () => Math.floor(Date.now() / 1000)

export function useDelayCountdown(delay) {
    const expiresAt = delayExpiresAtMs(delay)
    const nowSeconds = useSyncExternalStore(
        expiresAt == null ? subscribeToNothing : subscribeToSeconds,
        secondsNow,
    )
    const now = nowSeconds * 1000

    if (expiresAt == null) {
        return {remainingSeconds: 0, remainingMinutes: 0, expired: true, label: ''}
    }

    const remainingSeconds = Math.max(0, Math.round((expiresAt - now) / 1000))
    /* ⚠️ ROUNDED UP. Forty seconds left is "~1 min", not "~0 min" — a zero on a window
       that is still open reads as an expired delay and sends the passenger back to the
       picker. It reaches 0 only when the window actually has. */
    const remainingMinutes = Math.ceil(remainingSeconds / 60)

    return {
        remainingSeconds,
        remainingMinutes,
        expired: remainingSeconds <= 0,
        label: `~${remainingMinutes} min`,
    }
}

export default useDelayCountdown
